import { DatePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { DivListComponent } from "@components/list/div-list/div-list.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { Viol3statusIndicatorComponent } from "@components/viol3status-indicator/viol3status-indicator.component";
import { VirkesorderstatusIndicatorComponent } from "@components/virkesorderstatus-indicator/virkesorderstatus-indicator.component";
import moment from "moment";
import { Observable } from "rxjs";
import { Arbetsordertyper, Operationstyper, RaskRoller, VirkesorderStatus } from "~app/common/models/constants";
import { VillkorligtDatumDTO } from "~app/common/models/planera/VillkorligtDatumDTO";
import { TullkontorDTO } from "~app/common/models/tullkontor/TullkontorDto";
import { ITypeAheadResult, TypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { AuthenticationAuthority, AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FeatureService } from "~app/common/services/feature.service";
import { FormatService } from "~app/common/services/format.service";
import { prologLink } from "~app/common/utils/prolog-link";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ArbetsorderAtgardskedjaBehaviour } from "~app/features/planera/behaviours/ArbetsorderAtgardskedjaBehaviour";
import { SetAnsvarigAvverkningledareCommand } from "~app/features/planera/commands/SetAnsvarigAvverkningledareCommand";
import { SetArbetsorderBatIdCommand } from "~app/features/planera/commands/SetArbetsorderBatIdCommand";
import { SetArbetsorderUppdragCommand } from "~app/features/planera/commands/SetArbetsorderUppdragCommand";
import { SetArbetsorderVirketMarktCommand } from "~app/features/planera/commands/SetArbetsorderVirketMarktCommand";
import { SetCerifikatCommand } from "~app/features/planera/commands/SetCerifikatCommand";
import { SetTullkontorCommand } from "~app/features/planera/commands/SetTullkontorCommand";
import { UpdateArbetsorderNamnCommand } from "~app/features/planera/commands/UpdateArbetsorderNamnCommand";
import * as ArbetsorderConstants from "~app/features/planera/model/ArbetsorderConstants";
import { ArbetsorderDisabledFlag } from "~app/features/planera/model/ArbetsorderDisabledFlag";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { IAtgardskedjaKoppling } from "~app/features/planera/model/IAtgardskedja";
import { IUppdrag } from "~app/features/planera/model/IUppdrag";
import { ArbetsorderService, Produktionsstatusar } from "~app/features/planera/services/arbetsorder.service";
import { PlaneraService } from "~app/features/planera/services/planera.service";
import { ProduktionsstatusPipe } from "../../../../../common/pipes/produktionsstatus.pipe";
import { ArbetsorderViol3MakuleraButtonComponent } from "../../../arbetsorder-viol3/arbetsorder-viol3-makulera-button/arbetsorder-viol3-makulera-button.component";
import { AvslutaVirkesorderButtonComponent } from "../../../arbetsorder-virkesorder/avsluta-virkesorder-button/avsluta-virkesorder-button.component";
import { MakuleraArbetsorderButtonComponent } from "../../../makulera-arbetsorder-button/makulera-arbetsorder-button.component";
import { PlaneringsstatusIndicatorComponent } from "../../../planeringsstatus-indicator/planeringsstatus-indicator.component";

@Component({
    selector: "rask-arbetsorder-info-summary",
    templateUrl: "./arbetsorder-info-summary.component.html",
    styleUrls: ["./arbetsorder-info-summary.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputComponent,
        FormsModule,
        TextmultilineComponent,
        RouterLink,
        PlaneringsstatusIndicatorComponent,
        SelectorComponent,
        VirkesorderstatusIndicatorComponent,
        Viol3statusIndicatorComponent,
        AvslutaVirkesorderButtonComponent,
        ArbetsorderViol3MakuleraButtonComponent,
        MakuleraArbetsorderButtonComponent,
        CheckboxComponent,
        DivListComponent,
        ListColumnDirective,
        DatePipe,
        ProduktionsstatusPipe
    ]
})
export class ArbetsorderInfoSummaryComponent implements OnChanges {
    readonly arbetsorderService = inject(ArbetsorderService);
    readonly authenticationService = inject(AuthenticationService);
    readonly featureService = inject(FeatureService);
    readonly planeraService = inject(PlaneraService);
    readonly formatService = inject(FormatService);

    @Input() arbetsorder: IArbetsorderDetail;
    @Input() validationContext: ValidationContext;
    relateradArbetsorderAtgarder: IAtgardskedjaKoppling[];
    ansvarigAvverkningledareTypeAhead: ITypeAheadResult[];
    selectedansvarigAvverkningledareTypeAhead: ITypeAheadResult;
    avverkningsAnsvarig: ITypeAheadResult = null;
    tullkontorList: TullkontorDTO[];
    arbetsorderNamnMaxLength = ArbetsorderConstants.ArbetsordernamnMaxLength;
    uppdragsLista: IUppdrag[];
    uppdragsListaAktiv: IUppdrag[];
    eudrFeatureActive$: Observable<boolean>;

    ngOnChanges(changes: SimpleChanges): void {
        const arbetsorderAtgardskedja = new ArbetsorderAtgardskedjaBehaviour(this.arbetsorder);
        this.relateradArbetsorderAtgarder = arbetsorderAtgardskedja.getRelateradAtgardskoppling();

        if (this.arbetsorder.ansvarigAvverkningsledare && this.arbetsorder.ansvarigAvverkningsledare.id > 0) {
            const avverkningsledareNamn = `${this.arbetsorder.ansvarigAvverkningsledare.fornamn} ${this.arbetsorder.ansvarigAvverkningsledare.efternamn} - ` +
                `${this.arbetsorder.ansvarigAvverkningsledare.userName.toLowerCase()}`;

            this.avverkningsAnsvarig = new TypeAheadResult(avverkningsledareNamn, true, this.arbetsorder.ansvarigAvverkningsledare.id);
        }

        this.planeraService.getTullkontor().subscribe((tullkontor) => {
            this.tullkontorList = tullkontor;
        });

        if (!this.uppdragsLista) {
            this.planeraService.getUppdrag().subscribe((uppdrag) => {
                this.uppdragsLista = uppdrag;
                this.uppdragsListaAktiv = uppdrag.filter(x => x.aktiv);
            });
        }
    }

    ansvarigViewFormatter = (result: ITypeAheadResult) => result ? result.namn : undefined;
    ansvarigSelectedFormatter = (result: ITypeAheadResult) => result ? result.namn : undefined;
    ansvarigFormatter = (result: ITypeAheadResult) => result;

    tullkontorViewFormatter = (result: TullkontorDTO) => result ? `${result.kod} ${result.namn}` : undefined;
    tullkontorSelectedFormatter = (result: TullkontorDTO) => result ? `${result.kod} ${result.namn}` : undefined;
    tullkontorFormatter = (result: TullkontorDTO) => result;

    // eslint-disable-next-line sonarjs/no-nested-conditional
    uppdragViewFormatter = (result: IUppdrag) => result ? result.beskrivning !== result.kod ? `${result.beskrivning} (${result.kod})` : result.beskrivning : undefined;
    uppdragSelectedFormatter = (result: IUppdrag) => this.uppdragViewFormatter(result);
    uppdragFormatter = (result: IUppdrag) => result?.id;
    getUppdragInitialValue = (id: number) => this.uppdragsLista?.find(x => x.id === id);

    isRelateradArbetsorderGrot(): boolean {
        return this.relateradArbetsorderAtgarder.some(r => r.arbetsorderTyp === Arbetsordertyper.Grotskotning);
    }

    isDetailEditable = () => this.arbetsorder.isEditableForMode(ArbetsorderDisabledFlag.makulerad);

    isDetailEditableAfterSend = () => this.isDetailEditable() && !this.arbetsorder.skickadTillViol3;

    atgarderWithKoppladeGrotAtgarder(): IAtgard[] {
        return this.arbetsorder.atgarder.filter(x => x.atgardskedja?.atgardskedjaKopplingar
            .some(r => r.atgardId !== x.id && r.arbetsorderTyp === Arbetsordertyper.Grotskotning));
    }

    isRaskVo(): boolean {
        return this.arbetsorder?.raskVo;
    }

    canOpenVirkeskopskontrakt(): boolean {
        return this.authenticationService.hasRoles([
            RaskRoller.role.Kopleda,
            RaskRoller.role.VirkeskopTitta
        ]) ||
            (this.authenticationService.hasRoles(RaskRoller.role.Virkeskopa) && this.arbetsorder.virkeskopskontraktDetail.ansvarigVirkeskopareUserName &&
                AuthenticationAuthority.getUser().userName.toUpperCase() === this.arbetsorder.virkeskopskontraktDetail.ansvarigVirkeskopareUserName.toUpperCase());
    }

    isSchemalagd() {
        return this.arbetsorderService.isSchemalagd(this.arbetsorder.periodsattPeriod, this.arbetsorder.operationer);
    }

    canAvslutaVirkesorder(): boolean {
        if (!this.authenticationService.hasRoles(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        return this.arbetsorder.virkesorderStatus
            .some(x => x.status === VirkesorderStatus.Aktiv || x.status === VirkesorderStatus.FelLoadTender);
    }

    canAvslutaViol3Avtalsobjekt(): boolean {
        if (!this.arbetsorder.viol3) {
            return false;
        }

        if (!this.authenticationService.hasRoles(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        return true;
    }

    canMakuleraArbetsorder(): boolean {
        if (!this.authenticationService.hasRoles(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        const ejAvslutadVo = this.arbetsorder.virkesorderStatus
            .some(x => x.status === VirkesorderStatus.Aktiv ||
                x.status === VirkesorderStatus.FelLoadTender ||
                x.status === VirkesorderStatus.Sand) ||
            this.arbetsorder.skickadTillViol3;

        const makuleradVo = this.arbetsorder.virkesorderStatus.some(x => x.status === VirkesorderStatus.Makulerad) || (this.arbetsorder.viol3
            && this.arbetsorder.skickadTillViol);

        return this.arbetsorder.internProduktionsstatus === Produktionsstatusar.produktionEjPaborjad &&
            this.arbetsorder.isEditableForMode(ArbetsorderDisabledFlag.makulerad) &&
            !ejAvslutadVo &&
            makuleradVo;
    }

    prologLink = (): string => prologLink(this.arbetsorder.beteckning);

    isBetweenDateRange(villkorligtdatum: VillkorligtDatumDTO): boolean {

        if (villkorligtdatum.villkor === "SKS") {
            return !this.isSlutdatumExpired(villkorligtdatum);
        }

        const start = moment(villkorligtdatum.startdatum);
        const slut = moment(villkorligtdatum.slutdatum);

        const operationstyp = this.arbetsorder.isLeveransvirke
            ? Operationstyper.Skotning
            : Operationstyper.Skordning;

        const operation = this.arbetsorderService.getOperationInArbetsorder(this.arbetsorder.operationer, operationstyp);

        if (!operation) {
            return true;
        }

        const operationsdatumIsBeforeVillkorligtStartdatum = moment(operation.startdatum).isBefore(start, "day");
        const operationsdatumIsAfterVillkorligtSlutdatum = moment(operation.slutdatum).isAfter(slut, "day");

        if (operationsdatumIsBeforeVillkorligtStartdatum || operationsdatumIsAfterVillkorligtSlutdatum) {
            return false;
        }

        return true;
    }

    isSlutdatumExpired(villkorligtdatum: VillkorligtDatumDTO): boolean {

        const slutdatum = moment(villkorligtdatum.slutdatum);
        const today = moment(new Date());

        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (moment(slutdatum).isBefore(today, "day")) {
            return true;
        }

        return false;
    }

    searchForAnsvarigAvverningsledareTypeAhead = (input: string) => this.arbetsorderService.getArbetsorderAnsvarigAvverkningsledareTypeAhead(input);

    selectedAvverkningsledare(item: ITypeAheadResult) {
        if (item?.kod) {
            this.arbetsorder.commandManager.addOrUpdate(new SetAnsvarigAvverkningledareCommand(instance => {
                instance.arbetsorderId = this.arbetsorder.id;
                instance.username = item.kod;
            }), instance => instance.$type === SetAnsvarigAvverkningledareCommand.$type);

            this.arbetsorderService.getAnvandare(item.kod).then(x => {
                this.arbetsorder.ansvarigAvverkningsledare = x;
            });
        } else if (!item) {
            this.arbetsorder.commandManager.addOrUpdate(new SetAnsvarigAvverkningledareCommand(instance => {
                instance.arbetsorderId = this.arbetsorder.id;
                instance.username = null;
            }), instance => instance.$type === SetAnsvarigAvverkningledareCommand.$type);

            this.arbetsorder.ansvarigAvverkningsledare = null;
        }
    }

    setCertifikat = (selected: boolean) => {
        this.arbetsorder.commandManager.addOrUpdate(new SetCerifikatCommand(instance => {
            instance.arbetsorderId = this.arbetsorder.id;
            instance.fscCertifikat = this.arbetsorder.fscCertifikat;
            instance.pefcCertifikat = this.arbetsorder.pefcCertifikat;
        }), instance => instance.$type === SetCerifikatCommand.$type);
    };

    selectTullkontor = (item: TullkontorDTO) => {
        if (item?.id) {
            this.arbetsorder.commandManager.addOrUpdate(new SetTullkontorCommand(instance => {
                instance.arbetsorderId = this.arbetsorder.id;
                instance.tullkontorId = item.id;
            }), instance => instance.$type === SetTullkontorCommand.$type);
        }
    };

    arbetsorderNamnUpdated = () => {
        this.arbetsorder.commandManager.updateCommand(new UpdateArbetsorderNamnCommand((instance) => {
            instance.arbetsorderId = this.arbetsorder.id;
            instance.namn = this.arbetsorder.namn;
        }));
    };

    arbetsorderVirketMarktUpdated = () => {
        this.arbetsorder.commandManager.updateCommand(new SetArbetsorderVirketMarktCommand((instance) => {
            instance.arbetsorderId = this.arbetsorder.id;
            instance.virketMarkt = this.arbetsorder.virketMarkt;
        }));
    };

    batIdUpdated = () => {
        const cleanedBatId = this.arbetsorder.batId?.trim() ? this.arbetsorder.batId : null;
        this.arbetsorder.commandManager.updateCommand(new SetArbetsorderBatIdCommand((instance) => {
            instance.arbetsorderId = this.arbetsorder.id;
            instance.batId = cleanedBatId;
        }));
    };

    arbetsorderUppdragUpdated = (uppdragId: any) => {
        const cmd = new SetArbetsorderUppdragCommand((instance) => {
            instance.arbetsorderId = this.arbetsorder.id;
            instance.uppdragId = uppdragId;
        });
        this.arbetsorder.commandManager.addOrUpdate(cmd, (c) => c.$type === SetArbetsorderUppdragCommand.$type && c.arbetsorderId === this.arbetsorder.id);
    };

}
